import { inject, Injectable } from '@angular/core';

import { PrimitiveType, Validations } from '../../class/workflow.type';
import { LogicFnContext } from '../logic/logic.service';
import { StatefulService } from '@app/service/stateful.service';
import { DATE_REGEX, DECIMAL_REGEX, EMAIL_REGEX, NAME_REGEX, REMOVE_NON_NUMERIC_REGEX } from '@shared/constant/regex.const';

type ValidationFns = {
  [validationName in keyof Required<Validations>]: (
    _context: LogicFnContext,
    value: PrimitiveType,
    arg: Validations[keyof Validations]
  ) => boolean;
};

/**
 * Validation methods for every validation type on the Validations interface
 */
@Injectable({
  providedIn: 'root'
})
export class ValidationService implements ValidationFns {
  private readonly statefulService = inject(StatefulService);

  /**
   * Validate against a maximum number of decimal digits
   * @param allowedDecimalDigits validation argument
   */
  public allowedDecimals = (_context: LogicFnContext, allowedDecimalDigits: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return value === '' || String(value).replace(DECIMAL_REGEX, '').replace(REMOVE_NON_NUMERIC_REGEX, '').length > allowedDecimalDigits;
    }
  };

  /**
   * Validate email
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   */
  public email = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string') {
      return !value || !!value.match(EMAIL_REGEX);
    }
  };

  /**
   * Validate against a fixed number of digits
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param fixedDecimalDigits validation argument
   */
  public fixedDecimals = (_context: LogicFnContext, fixedDecimalDigits: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !value || String(value).replace(DECIMAL_REGEX, '').replace(REMOVE_NON_NUMERIC_REGEX, '').length !== fixedDecimalDigits;
    }
  };

  /**
   * Check if value is letter only
   */
  public lettersOnly = (_context: LogicFnContext): boolean => {
    let value = this.getValue(_context);
    if (typeof value === 'string') {
    // Normalize hidden characters and smart quotes
    // Protects against strange hidden character iOS may insert
    value = value
      .normalize('NFKD')
      .replace(/[\u2018\u2019\u201A\u201B]/g, "'")  // curly to straight apostrophes
      .replace(/[\u201C\u201D\u201E]/g, '"')      // curly to straight quotes
      .replace(/[\u200B-\u200D\uFEFF]/g, '')     // zero-width chars
      .trim();                                    // trim whitespace (including non-breaking spaces)
      return !value || !!value.match(NAME_REGEX);
    }
  };

  /**
   * Validate if a value between min and max
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param max validation argument
   * @param min validation argument
   */
  public between = (_context: LogicFnContext, min: number, max: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      const numValue = this.statefulService.numericValue(value);
      const numMin = this.statefulService.numericValue(min);
      const numMax = this.statefulService.numericValue(max);
      return !(value || value === 0) || (numMin <= numValue && numValue <= numMax);
    }
  };

  /**
   * Validate against a maximum
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param max validation argument
   */
  public max = (_context: LogicFnContext, max: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || this.statefulService.numericValue(value) <= this.statefulService.numericValue(max);
    }
  };

  /**
   * Check value against a maximum length
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param maxLength validation argument
   */
  public maxLength = (_context: LogicFnContext, maxLength: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || String(value).length <= maxLength;
    }
  };

  /**
   * Check value against a minimum
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param min validation argument
   */
  public min = (_context: LogicFnContext, min: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || this.statefulService.numericValue(value) >= this.statefulService.numericValue(min);
    }
  };

  /**
   * Check value against a minimum length
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param minLength validation argument
   */
  public minLength = (_context: LogicFnContext, minLength: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || String(value).length >= minLength;
    }
  };

  /**
   * Check if value is date and in the past
   */
  public past = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    const validDate = this.date(_context);
    if (validDate && typeof value === 'string') {
      const date = new Date(this.dateString(value));
      return date <= new Date();
    }
  };

  /**
   * Date validation for future dates
   */
  public futureDate = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    const validDate = this.date(_context);

    // eslint-disable-next-line eqeqeq
    if (value == null || value == '') {
      return true;
    }
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (validDate && typeof value === 'string') {
      const enteredDate = new Date(this.dateString(value));
      return enteredDate >= today;
    }
  };
  /**
   * Check if value is a date
   */
  public date = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string') {
      if (value.length > 0 && value.length <= 2) {
        return false;
      }
      if (value.split('/').length > 1) {
        return DATE_REGEX.test(value);
      }
    }
  };

  /**
   * Check if value is exactly `true`
   * @param value value to hold against the validation requirement
   */
  public requireTrue = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'boolean') {
      return value;
    }
  };

  /**
   * Check if value is non-empty
   * @param value value to hold against the validation requirement
   */
  public required = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string') {
      return !!value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      return value != null;
    } else if (value == null) {
      return false;
    }
  };

  /**
   * Check value is greater than
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param min validation argument
   */
  public greater = (_context: LogicFnContext, min: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || this.statefulService.numericValue(value) > this.statefulService.numericValue(min);
    }
  };

  private getValue = (_context: LogicFnContext): PrimitiveType => {
    const data = _context.value || (_context.field && _context.field.data);
    if (!data) {
      throw Error(`Validation '${_context.functionName}' used in a wrong context. It has to be used either in field or stateful context.`);
    }
    return data.value;
  };

  private dateString = (value: string): string => {
    const [month, day, year] = this.dateParts(value);
    return `${month}/${day}/${year}`;
  };

  private dateParts = (value: string): [number, number, number] => {
    const parts = value.split('/');
    const month = +parts.shift();
    let day = 1;
    let year = 0;
    if (parts.length === 1) {
      year = +parts.shift();
    } else {
      day = +parts.shift();
      year = +parts.shift();
    }
    return [month, day, year];
  };


  // May need ot cleans iOS input before saving to the API
  // Not wired up yet, but may need it at some point
  public cleanInvisibleCharacters(value: string): string {
    if (!value) return value;

    // Normalize Unicode (important for accented chars, etc.)
    value = value.normalize('NFKC');

    // Remove invisible Unicode chars commonly inserted by iOS / spellcheck
    // Includes: Zero-width space, joiners, no-break spaces, soft hyphens, etc.
    return value
      .replace(/[\u200B-\u200D\uFEFF\u00A0\u2060\u180E\u202F]/g, '')
      .trim();
  }

}
